import React, { useEffect, useMemo, useState } from "react";
import { tasksApi, entitiesApi, projectsApi, contactsApi } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Wand2, Settings2 } from "lucide-react";
import TaskDialog from "@/components/TaskDialog";
import UniversalQuickAdd from "@/components/UniversalQuickAdd";
import { TasksList } from "@/pages/EntityWorkspace";
import { toast } from "sonner";

// Convert a Quick Add draft record into the shape TaskDialog expects (as an item preset without id).
export function draftToTaskPreset(rec) {
  const f = rec.fields || {};
  return {
    // No id → dialog treats as "new"
    entity_id: f.entity_id || "",
    project_id: f.project_id || "",
    name: f.title || "",
    description: f.description || "",
    status: f.status || "Not started",
    priority: f.priority || "Normal",
    due_date: f.due_date || "",
    assignee_contact_id: f.assignee_contact_id || "",
    reference_url: f.reference_url || "",
    related_contact_id: f.related_contact_id || "",
    tags: f.tags || [],
  };
}

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [entityFilter, setEntityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dueFilter, setDueFilter] = useState("all");
  const [dlg, setDlg] = useState({ open: false, task: null });
  const [quickOpen, setQuickOpen] = useState(false);

  const load = async () => {
    const [t, e, p, c] = await Promise.all([tasksApi.list(), entitiesApi.list(), projectsApi.list(), contactsApi.list()]);
    setTasks(t.data); setEntities(e.data); setProjects(p.data); setContacts(c.data);
  };
  useEffect(() => { load(); }, []);

  const entityById = useMemo(() => Object.fromEntries(entities.map(e => [e.id, e])), [entities]);

  const filtered = useMemo(() => {
    const today = new Date().toISOString().slice(0,10);
    const in7 = new Date(Date.now() + 7*24*3600*1000).toISOString().slice(0,10);
    return tasks.filter(t => {
      if (entityFilter !== "all" && t.entity_id !== entityFilter) return false;
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (dueFilter === "overdue") return t.due_date && t.due_date < today && t.status !== "Done";
      if (dueFilter === "today") return t.due_date === today;
      if (dueFilter === "week") return t.due_date && t.due_date >= today && t.due_date <= in7;
      return true;
    });
  }, [tasks, entityFilter, statusFilter, priorityFilter, dueFilter]);

  const active = filtered.filter(t => t.status !== "Done");
  const done = filtered.filter(t => t.status === "Done");

  const handleEditDetails = (rec) => {
    if (rec.type !== "task") { toast.error("Advanced form only supports tasks from this section"); return; }
    setDlg({ open: true, task: draftToTaskPreset(rec) });
  };

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="tasks-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-eyebrow mb-2">Executive View</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">All Tasks</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setQuickOpen(true)} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="new-task-btn">
            <Wand2 className="w-4 h-4 mr-2"/>Quick Add
          </Button>
          <Button variant="ghost" onClick={() => setDlg({ open: true, task: null })} className="text-white/70 hover:text-white hover:bg-white/10 text-xs" data-testid="new-task-advanced-btn">
            <Settings2 className="w-3.5 h-3.5 mr-1.5"/>Advanced form
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="task-filters">
        <FilterSelect value={entityFilter} onChange={setEntityFilter} placeholder="Entity" options={[{ value: "all", label: "All entities" }, ...entities.map(e => ({ value: e.id, label: e.name }))]}/>
        <FilterSelect value={statusFilter} onChange={setStatusFilter} placeholder="Status" options={[{ value: "all", label: "All statuses" }, ..."Open,In Progress,Blocked,Done".split(",").map(v => ({ value: v, label: v }))]}/>
        <FilterSelect value={priorityFilter} onChange={setPriorityFilter} placeholder="Priority" options={[{ value: "all", label: "All priorities" }, ..."Low,Medium,High,Critical".split(",").map(v => ({ value: v, label: v }))]}/>
        <FilterSelect value={dueFilter} onChange={setDueFilter} placeholder="Due" options={[{value:"all",label:"Any date"},{value:"overdue",label:"Overdue"},{value:"today",label:"Today"},{value:"week",label:"Next 7 days"}]}/>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="font-display text-lg mb-3 text-white/80">Active <span className="text-white/40 text-sm">({active.length})</span></h2>
          <TasksList
            tasks={active.map(t => ({ ...t, entity_name: entityById[t.entity_id]?.name }))}
            contacts={contacts} projects={projects}
            onEdit={(t) => setDlg({ open: true, task: t })}
            onDelete={async (t) => { if(window.confirm("Delete task?")){ await tasksApi.remove(t.id); toast.success("Deleted"); load();} }}
            onToggle={async (t) => { await tasksApi.update(t.id, { status: t.status === "Done" ? "Open" : "Done" }); load(); }}
            canDelete={(t) => {
              const p = (projects || []).find(pp => pp.id === t.project_id);
              return !!(p?.permissions?.is_admin || p?.permissions?.edit_project_settings);
            }}
          />
        </section>
        <section>
          <h2 className="font-display text-lg mb-3 text-white/80">Completed <span className="text-white/40 text-sm">({done.length})</span></h2>
          <TasksList
            tasks={done} contacts={contacts} projects={projects}
            onEdit={(t) => setDlg({ open: true, task: t })}
            onDelete={async (t) => { if(window.confirm("Delete task?")){ await tasksApi.remove(t.id); toast.success("Deleted"); load();} }}
            onToggle={async (t) => { await tasksApi.update(t.id, { status: "Open" }); load(); }}
            canDelete={(t) => {
              const p = (projects || []).find(pp => pp.id === t.project_id);
              return !!(p?.permissions?.is_admin || p?.permissions?.edit_project_settings);
            }}
          />
        </section>
      </div>

      <TaskDialog
        open={dlg.open}
        setOpen={(v) => setDlg({ ...dlg, open: v })}
        task={dlg.task}
        entityId={dlg.task?.entity_id || (entityFilter !== "all" ? entityFilter : entities[0]?.id)}
        projects={projects.filter(p => p.entity_id === (dlg.task?.entity_id || (entityFilter !== "all" ? entityFilter : entities[0]?.id)))}
        onSaved={() => { setDlg({ open: false, task: null }); load(); }}
      />
      <UniversalQuickAdd
        open={quickOpen} setOpen={setQuickOpen}
        section="tasks"
        defaultEntityId={entityFilter !== "all" ? entityFilter : null}
        onCreated={load}
        onEditDetails={handleEditDetails}
      />
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-sm h-9"><SelectValue placeholder={placeholder}/></SelectTrigger>
      <SelectContent className="bg-[#151A23] border-white/10 text-white">
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
